/**
 * Google Maps API 키 로테이션 및 스크립트 로드 유틸리티
 *
 * 주요 기능:
 * 1. 여러 API 키를 순차적으로 시도하여 할당량 초과 시 자동 전환
 * 2. 모든 키 소진 시 localStorage에 플래그 저장 (불필요한 API 호출 방지)
 * 3. 매일 17시 기준으로 소진 플래그 자동 초기화
 *
 * 사용 흐름:
 * - LoadGoogleMaps() 호출 → 스크립트 로드 성공 시 google.maps 사용 가능
 * - 맵 생성 후 OverQuotaMapError 발생 시 → retryWithNextKey() 호출
 * - 모든 키 소진 시 → setKeysExhausted() 호출하여 플래그 저장
 */

/**
 * 환경변수에서 Google Maps API 키 배열 생성
 * - 우선순위: KEY_3 → KEY_2 → KEY_1 순으로 시도
 * - filter(Boolean): 설정되지 않은 키(undefined) 제거
 */
const API_KEYS = [
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY_1,
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY_2,
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY_3,
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY_4,
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY_5,
].filter(Boolean);

// ============================================================================
// 모듈 레벨 상태 (싱글톤 패턴으로 전역 상태 관리)
// ============================================================================

/** 현재 사용 중인 API 키의 인덱스 (0부터 시작) */
let currentKeyIndex = 0;

/** Google Maps 스크립트 로드 완료 여부 */
let isLoaded = false;

/** 중복 로드 방지를 위한 Promise 캐시 (로딩 중일 때 같은 Promise 반환) */
let loadPromise: Promise<void> | null = null;

/** localStorage에 저장할 키 소진 플래그의 키 이름 */
const STORAGE_KEY = 'googleMapKeysExhausted';

/**
 * 모든 API 키 소진 여부 확인 (17시 기준 자동 초기화)
 *
 * 동작 원리:
 * 1. localStorage에서 소진 플래그(타임스탬프) 조회
 * 2. 플래그가 없으면 → 사용 가능 (false 반환)
 * 3. 플래그가 있으면 → 17시 기준 초기화 조건 확인:
 *    - 저장 날짜가 오늘 이전 → 초기화
 *    - 저장 시간이 오늘 17시 이전이고 현재가 17시 이후 → 초기화
 * 4. 초기화 시 모듈 상태(currentKeyIndex, isLoaded, loadPromise)도 리셋
 *
 * @returns true: 키 소진됨 (지도 사용 불가), false: 사용 가능
 */
export const isKeysExhausted = (): boolean => {
  const savedTime = localStorage.getItem(STORAGE_KEY);
  if (!savedTime) return false;

  const savedDate = new Date(Number(savedTime));
  const now = new Date();

  // 오늘 17시 계산
  const resetTime = new Date();
  resetTime.setHours(17, 0, 0, 0);

  // 저장 시간이 오늘 17시 이전이고, 현재가 17시 이후면 → 초기화
  // 또는 저장 시간이 어제 이전이면 → 초기화
  const savedDateOnly = new Date(
    savedDate.getFullYear(),
    savedDate.getMonth(),
    savedDate.getDate()
  );
  const todayOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (savedDateOnly < todayOnly || (savedDate < resetTime && now >= resetTime)) {
    localStorage.removeItem(STORAGE_KEY);
    // 상태 초기화
    currentKeyIndex = 0;
    isLoaded = false;
    loadPromise = null;
    return false;
  }

  return true;
};

/**
 * 키 소진 플래그를 localStorage에 저장
 *
 * 호출 시점:
 * - 모든 API 키를 시도했으나 전부 OverQuotaMapError 발생 시
 * - BoxLocationMap.tsx의 handleRetry()에서 retryWithNextKey()가 false 반환 시 호출
 *
 * 저장 값: 현재 타임스탬프 (17시 기준 초기화 판단에 사용)
 */
export const setKeysExhausted = (): void => {
  localStorage.setItem(STORAGE_KEY, Date.now().toString());
};

/**
 * Google Maps 스크립트를 동적으로 로드
 *
 * 주의사항:
 * - 기존 스크립트가 있으면 제거 후 새로 로드 (키 전환 시 필수)
 * - window.google.maps 객체도 삭제해야 새 키로 초기화됨
 * - JSONP 콜백 방식 사용 (callback 파라미터)
 *
 * @param apiKey - 사용할 Google Maps API 키
 * @returns 스크립트 로드 완료 시 resolve되는 Promise
 */
const loadMapsScript = (apiKey: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    // 기존 Google Maps 스크립트가 있으면 제거 (키 전환 시 필수)
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) existingScript.remove();

    // window.google.maps 객체도 삭제해야 새 키로 완전히 초기화됨
    if ((window as any).google?.maps) {
      delete (window as any).google.maps;
    }

    // 유니크한 콜백 이름 생성 (동시 로드 충돌 방지)
    const callbackName = `googleMapsCallback_${Date.now()}`;

    // JSONP 콜백 등록 - 스크립트 로드 완료 시 호출됨
    (window as any)[callbackName] = () => {
      delete (window as any)[callbackName]; // 콜백 정리
      resolve();
    };

    // 스크립트 엘리먼트 생성 및 설정
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=${callbackName}`;
    script.async = true;
    script.defer = true;

    // 스크립트 로드 실패 시 (네트워크 에러 등)
    script.onerror = () => {
      delete (window as any)[callbackName];
      reject(new Error('스크립트 로드 실패'));
    };

    document.head.appendChild(script);
  });
};

/**
 * Google Maps API 로드 (외부에서 호출하는 메인 함수)
 *
 * 로드 전략:
 * 1. 이미 로드됨 → 즉시 resolve
 * 2. 로딩 중 → 기존 Promise 반환 (중복 요청 방지)
 * 3. 미로드 → currentKeyIndex부터 순차적으로 키 시도
 * 4. 모든 키 실패 → '모든 API 키 소진' 에러 throw
 *
 * 참고: 이 함수는 스크립트 로드 에러만 감지함
 * 맵 생성 후 발생하는 OverQuotaMapError는 MutationObserver에서 별도 처리 필요
 */
export const LoadGoogleMaps = async (): Promise<void> => {
  // 이미 로드 완료된 경우 즉시 반환
  if (isLoaded && window.google?.maps?.Map) {
    return Promise.resolve();
  }

  // 로딩 중인 경우 기존 Promise 반환 (중복 로드 방지)
  if (loadPromise) {
    return loadPromise;
  }

  // 새로운 로드 시작
  loadPromise = (async () => {
    // currentKeyIndex부터 순차적으로 키 시도
    for (let i = currentKeyIndex; i < API_KEYS.length; i++) {
      const apiKey = API_KEYS[i];

      try {
        await loadMapsScript(apiKey as string);

        // 로드 성공
        isLoaded = true;
        currentKeyIndex = i;
        return;
      } catch {
        // 이 키로 실패 → 다음 키 시도
        continue;
      }
    }

    // 모든 키로 시도했으나 전부 실패
    throw new Error('모든 API 키 소진');
  })();

  return loadPromise;
};

/**
 * 다음 API 키로 전환하여 재시도
 *
 * 호출 시점: 맵 생성 후 OverQuotaMapError 감지 시 (BoxLocationMap의 MutationObserver에서 호출)
 *
 * 동작:
 * 1. currentKeyIndex 증가
 * 2. 더 이상 키가 없으면 false 반환
 * 3. 상태 초기화 후 LoadGoogleMaps() 재호출
 *
 * @returns true: 재시도 성공 (다음 키로 로드됨), false: 모든 키 소진
 */
export const retryWithNextKey = async (): Promise<boolean> => {
  // 다음 키로 이동
  currentKeyIndex++;

  // 더 이상 시도할 키가 없음
  if (currentKeyIndex >= API_KEYS.length) {
    return false;
  }

  // 상태 초기화 (새로운 키로 다시 로드하기 위해)
  isLoaded = false;
  loadPromise = null;

  try {
    await LoadGoogleMaps();
    return true; // 재시도 성공
  } catch {
    return false; // 재시도도 실패
  }
};

// ============================================================================
// 맵 인스턴스 + DOM 전역 관리 (싱글톤)
// API 호출 최소화를 위해 맵 인스턴스와 DOM 컨테이너를 React 라이프사이클 바깥에서 관리
// ============================================================================

/** 전역 맵 인스턴스 (최초 생성 후 재사용) */
let globalMapInstance: google.maps.Map | null = null;

/** 전역 맵 컨테이너 DOM (React 언마운트 시에도 보존됨) */
let globalMapContainer: HTMLDivElement | null = null;

/**
 * 전역 맵 초기화 (최초 1회만 맵 생성, 이후 재사용)
 *
 * 동작 원리:
 * 1. 이미 맵이 있으면 → 기존 인스턴스 반환 (API 호출 없음)
 * 2. 맵이 없으면 → 스크립트 로드 후 맵 생성 (API 호출 발생)
 *
 * @param options - Google Maps 초기화 옵션
 * @returns 맵 인스턴스
 */
export const initializeGlobalMap = async (
  options: google.maps.MapOptions
): Promise<google.maps.Map> => {
  // 이미 맵이 있으면 재사용
  if (globalMapInstance && globalMapContainer) {
    return globalMapInstance;
  }

  // Google Maps 스크립트 로드
  await LoadGoogleMaps();

  // 맵 컨테이너 생성
  globalMapContainer = document.createElement('div');
  globalMapContainer.id = 'global-map-container';
  globalMapContainer.style.width = '100%';
  globalMapContainer.style.height = '100%';

  // holder에 추가 (React 라이프사이클 바깥)
  const holder = document.getElementById('global-map-holder');
  if (holder) {
    holder.appendChild(globalMapContainer);
  }

  // 맵 생성 (API 호출 - 과금 발생)
  globalMapInstance = new google.maps.Map(globalMapContainer, options);

  return globalMapInstance;
};

/**
 * 맵 DOM을 특정 요소로 이동 (화면에 표시)
 *
 * 동작 원리:
 * - appendChild()는 DOM을 이동시킴 (복사가 아님)
 * - 이동 후 resize 이벤트 트리거하여 맵이 새 컨테이너 크기에 맞게 조정됨
 *
 * @param targetEl - 맵을 표시할 타겟 컨테이너
 */
export const attachMapTo = (targetEl: HTMLElement): void => {
  if (!globalMapContainer) {
    return;
  }

  targetEl.appendChild(globalMapContainer);

  // 컨테이너 크기에 맞게 리사이즈
  if (globalMapInstance) {
    google.maps.event.trigger(globalMapInstance, 'resize');
  }
};

/**
 * 맵 DOM을 holder로 복귀 (숨김)
 *
 * 호출 시점: 컴포넌트 언마운트 시 cleanup에서 호출
 * 주의: 맵을 파괴하지 않음 - 다음 마운트 시 재사용 가능
 */
export const detachMap = (): void => {
  const holder = document.getElementById('global-map-holder');
  if (!holder || !globalMapContainer) {
    return;
  }

  holder.appendChild(globalMapContainer);
};

/**
 * 맵 인스턴스 반환
 *
 * @returns 현재 전역 맵 인스턴스 (없으면 null)
 */
export const getMapInstance = (): google.maps.Map | null => {
  return globalMapInstance;
};

/**
 * 맵 완전 파괴 (에러 시 또는 로그아웃 시)
 *
 * 호출 시점:
 * - OverQuotaMapError 발생 시 (다음 키로 재시도 전)
 * - 사용자 로그아웃 시 (선택적)
 */
export const destroyGlobalMap = (): void => {
  if (globalMapContainer) {
    globalMapContainer.remove();
  }
  globalMapInstance = null;
  globalMapContainer = null;
};
